/**
 * Parse the `# Key: Value` comment block at the top of a Bypass CSV to
 * pre-fill the form metadata. Used by Bypass.tsx as soon as the user
 * drops a CSV — saves them retyping fields Claude.ai already extracted.
 *
 * Matches the prompt-template format we ship in ~/Downloads/bypass-template.csv.
 * Tolerant: ignores decoration lines (`# ====`), missing keys, `<MISSING>`
 * placeholders, varying whitespace.
 */

import { BypassMetadata } from '../../services/bypassApi';

const KEY_MAP: Record<string, keyof BypassMetadata> = {
  'order id': 'orderId',
  'orderid': 'orderId',
  'patient name': 'patientName',
  'patient age': 'patientAge',
  'patient gender': 'patientGender',
  'test date': 'testDate',
  'lab name': 'labName',
};

function normGender(v: string): 'male' | 'female' | 'other' | '' {
  const s = v.trim().toLowerCase();
  if (['m', 'male'].includes(s)) return 'male';
  if (['f', 'female'].includes(s)) return 'female';
  if (['o', 'other'].includes(s)) return 'other';
  return '';
}

function normDate(v: string): string {
  const s = v.trim();
  if (!s) return '';
  // accept YYYY-MM-DD as-is; otherwise try Date parsing
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  return '';
}

function normAge(v: string): number {
  const m = v.trim().match(/(\d+)/);
  return m ? parseInt(m[1], 10) : 0;
}

/**
 * Read the comment block of the CSV file and return parsed metadata.
 * Returns only fields that were found AND non-empty AND non-MISSING.
 */
export async function parseMetadataFromCsv(file: File): Promise<Partial<BypassMetadata>> {
  const text = await file.text();
  const out: Partial<BypassMetadata> = {};
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    if (!line.startsWith('#')) break; // data row reached — stop scanning
    // strip leading '#' and spaces
    const stripped = line.replace(/^#\s*/, '');
    // skip decoration lines like "====" or section headers
    if (!stripped.includes(':')) continue;
    const colonIdx = stripped.indexOf(':');
    const key = stripped.slice(0, colonIdx).trim().toLowerCase();
    const value = stripped.slice(colonIdx + 1).trim();
    if (!value || /^<MISSING>$/i.test(value) || /^<fill/i.test(value)) continue;
    const field = KEY_MAP[key];
    if (!field) continue;
    switch (field) {
      case 'patientAge':
        out.patientAge = normAge(value);
        break;
      case 'patientGender': {
        const g = normGender(value);
        if (g) out.patientGender = g;
        break;
      }
      case 'testDate': {
        const d = normDate(value);
        if (d) out.testDate = d;
        break;
      }
      default:
        (out as any)[field] = value;
    }
  }
  return out;
}
